import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { ErrorNote, Field, GoldButton, Strength, authErr } from "@/components/login/AuthBits";
import { BrandLogo } from "@/components/BrandLogo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [p, setP] = useState("");
  const [c, setC] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const go = async (e) => {
    e.preventDefault(); setErr("");
    if (p.length < 8) return setErr("Use at least 8 characters.");
    if (p !== c) return setErr("The two passwords don't match.");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: p }); setDone(true); }
    catch (x) { setErr([400, 401, 410].includes(x?.response?.status) ? "This reset link has expired or was already used. Request a new one from the sign-in page." : authErr(x)); }
    finally { setBusy(false); }
  };
  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-clip bg-[#09090B] p-4">
      <div className="lp-glow pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#D4AF37]/10 blur-[140px]" />
      <div className="lp-glow pointer-events-none absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#8224e3]/10 blur-[140px]" />
      <div className="signin-card fade-up relative w-full max-w-md rounded-[28px] p-[1.5px]" data-testid="reset-card">
        <div className="rounded-[27px] bg-[#0E0E11]/95 p-6 backdrop-blur-xl sm:p-8">
          <Link to="/login"><BrandLogo size="md" className="mb-6" /></Link>
          {!token ? <div data-testid="reset-missing"><p className="font-display text-xl text-zinc-50">This link isn't complete</p><p className="mt-2 text-sm text-zinc-400">Open the link from your email again, or request a new one.</p><Link to="/login" className="mt-5 inline-block text-sm text-[#D4AF37] hover:text-[#F3C649]" data-testid="reset-to-login">Back to sign in</Link></div>
            : done ? <div className="auth-in text-center" data-testid="reset-done">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-300"><CheckCircle2 className="h-6 w-6" /></span>
              <p className="mt-4 font-display text-xl text-zinc-50">Password updated</p><p className="mt-1 text-sm text-zinc-400">You can now sign in with your email and new password.</p>
              <button onClick={() => nav("/login")} className="mt-6 h-12 w-full rounded-xl bg-gradient-to-r from-[#D4AF37] via-[#F3C649] to-[#C9A230] text-sm font-semibold text-[#09090B]" data-testid="reset-go-signin">Go to sign in</button>
            </div>
            : <form onSubmit={go} className="space-y-4" data-testid="reset-form">
              <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#D4AF37]/40 bg-[#33290D] text-[#F3C649]"><KeyRound className="h-5 w-5" /></span><div><p className="font-display text-xl text-zinc-50">Set a new password</p><p className="text-xs text-zinc-500">At least 8 characters.</p></div></div>
              <div><Field label="New password" type="password" value={p} onChange={(x) => { setP(x); setErr(""); }} testid="reset-password" autoComplete="new-password" autoFocus /><Strength p={p} /></div>
              <Field label="Confirm password" type="password" value={c} onChange={(x) => { setC(x); setErr(""); }} testid="reset-confirm" autoComplete="new-password" />
              <ErrorNote msg={err} />
              <GoldButton busy={busy} testid="reset-submit">Update password</GoldButton>
            </form>}
        </div>
      </div>
    </div>
  );
}
